import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import UserAvatar from '../components/UserAvatar';
import useJevSuggest from '../hooks/useJevSuggest';
import { 
  ClipboardCheck, 
  Plus, 
  Play, 
  CheckCircle, 
  Clock, 
  FileText, 
  Building2,
  ChevronRight,
  Calendar,
  User,
  AlertTriangle,
  Sparkles,
  Star,
  Loader2,
  X,
  Camera,
  Upload
} from 'lucide-react';
import { AUDIT_QUESTIONS, getTotalItems, calculateAuditScore } from '../data/auditQuestions';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIT_TYPES = [
  { 
    id: 'daily-walkthrough', 
    name: 'Daily Walk-Thru', 
    description: 'Daily Operations Excellence - FRONT & KITCHEN walkthrough',
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50 border-blue-200',
    icon: ClipboardCheck
  },
  { 
    id: 'self-assessment', 
    name: 'Restaurant Self Assessment', 
    description: 'Comprehensive self-evaluation: Food Safety, Food Quality, Clean & Tidy, People, Service & Speed, Brand Standards',
    color: 'bg-green-500',
    lightColor: 'bg-green-50 border-green-200',
    icon: FileText
  },
  { 
    id: 'bca', 
    name: 'Brand Compliance Assessment (BCA)', 
    description: 'A&W Brand Compliance: 5 Star Operating System, Service, Clean, Food Quality, Safety, Training, Brand Standards',
    color: 'bg-orange-500',
    lightColor: 'bg-orange-50 border-orange-200',
    icon: Star
  },
  { 
    id: 'steritech', 
    name: 'Steritech Self Assessment', 
    description: 'Food safety audit with Critical/Second/Third Priority violations and A&W specific items',
    color: 'bg-red-500',
    lightColor: 'bg-red-50 border-red-200',
    icon: AlertTriangle
  },
  { 
    id: 'be-clean', 
    name: 'Be Clean Assessment', 
    description: 'Monthly, Quarterly, Bi-Annual, and Annual cleaning and maintenance checklist',
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50 border-purple-200',
    icon: CheckCircle
  },
];

export default function Audits() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  const [audits, setAudits] = useState([]);
  const [inProgressAudits, setInProgressAudits] = useState([]);
  const [completedAudits, setCompletedAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedAuditType, setSelectedAuditType] = useState('');
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [currentAudit, setCurrentAudit] = useState(null);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [auditsRes, storesRes] = await Promise.all([
        axios.get(`${API_URL}/audits`),
        axios.get(`${API_URL}/stores`)
      ]);
      
      const allAudits = auditsRes.data || [];
      setAudits(allAudits);
      setInProgressAudits(allAudits.filter(a => a.status === 'in_progress'));
      setCompletedAudits(allAudits.filter(a => a.status === 'completed'));
      setStores(storesRes.data || []);
      
      if (storesRes.data?.length > 0) {
        setSelectedStore(storesRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAudit = async () => {
    if (!selectedStore || !selectedAuditType) {
      toast.error('Please select a store and audit type');
      return;
    }

    try {
      const totalItems = getTotalItems(selectedAuditType);
      const res = await axios.post(`${API_URL}/audits`, {
        store_id: selectedStore,
        audit_type: selectedAuditType,
        total_items: totalItems
      });
      
      toast.success('Audit started!');
      setStartDialogOpen(false);
      setCurrentAudit(res.data);
      setAuditDialogOpen(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to start audit');
    }
  };

  const handleContinueAudit = (audit) => {
    setCurrentAudit(audit);
    setAuditDialogOpen(true);
  };

  const handleSaveAudit = async (responses, complete = false, actionPlans = {}, photos = {}) => {
    if (!currentAudit) return;

    try {
      await axios.put(`${API_URL}/audits/${currentAudit.id}`, {
        responses,
        action_plans: actionPlans,
        photos,
        status: complete ? 'completed' : 'in_progress'
      });
      
      toast.success(complete ? 'Audit completed!' : 'Progress saved');
      if (complete) {
        setAuditDialogOpen(false);
        setCurrentAudit(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to save audit');
    }
  };

  const getAuditTypeInfo = (typeId) => {
    return AUDIT_TYPES.find(t => t.id === typeId) || AUDIT_TYPES[0];
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'Unknown Store';
  };

  const calculateProgress = (audit) => {
    if (!audit.responses || !audit.total_items) return 0;
    return Math.round((Object.keys(audit.responses).length / audit.total_items) * 100);
  };

  return (
    <div className="space-y-6" data-testid="audits-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Audits
          </h1>
          <p className="text-slate-500 mt-1">Perform and track restaurant audits</p>
        </div>
        <Button 
          onClick={() => setStartDialogOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
          data-testid="start-audit-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Start New Audit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-slate-200 rounded-xl">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{inProgressAudits.length}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-xl">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{completedAudits.length}</p>
              <p className="text-sm text-slate-500">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-xl">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stores.length}</p>
              <p className="text-sm text-slate-500">Stores</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="new" className="rounded-lg data-[state=active]:bg-white">
            Start New
          </TabsTrigger>
          <TabsTrigger value="in-progress" className="rounded-lg data-[state=active]:bg-white">
            In Progress ({inProgressAudits.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-lg data-[state=active]:bg-white">
            Completed ({completedAudits.length})
          </TabsTrigger>
        </TabsList>

        {/* Start New Tab */}
        <TabsContent value="new" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIT_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <Card 
                  key={type.id}
                  className={`cursor-pointer transition-all border-2 hover:shadow-md ${type.lightColor}`}
                  onClick={() => {
                    setSelectedAuditType(type.id);
                    setStartDialogOpen(true);
                  }}
                  data-testid={`audit-type-${type.id}`}
                >
                  <CardContent className="p-5">
                    <div className={`w-12 h-12 ${type.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{type.name}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{type.description}</p>
                    <div className="mt-4 flex items-center text-orange-600 text-sm font-medium">
                      Start Audit <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* In Progress Tab */}
        <TabsContent value="in-progress" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : inProgressAudits.length === 0 ? (
            <Card className="border border-slate-200 rounded-xl">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No audits in progress</p>
                <Button 
                  variant="link" 
                  onClick={() => setActiveTab('new')}
                  className="mt-2 text-orange-600"
                >
                  Start a new audit
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {inProgressAudits.map((audit) => {
                const typeInfo = getAuditTypeInfo(audit.audit_type);
                const progress = calculateProgress(audit);
                const Icon = typeInfo.icon;
                return (
                  <Card key={audit.id} className="border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 ${typeInfo.color} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900">{typeInfo.name}</h3>
                            <p className="text-sm text-slate-500">{getStoreName(audit.store_id)}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(audit.created_at), 'MMM d, yyyy')}
                              </span>
                              <span className="flex items-center gap-1.5" data-testid={`audit-creator-${audit.id}`}>
                                <UserAvatar name={audit.created_by} picture={audit.created_by_picture} size="xs" />
                                {audit.created_by}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button 
                          onClick={() => handleContinueAudit(audit)}
                          className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                        >
                          <Play className="w-4 h-4 mr-2" /> Continue
                        </Button>
                      </div>
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-slate-500">Progress</span>
                          <span className="font-medium text-slate-900">{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Completed Tab */}
        <TabsContent value="completed" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : completedAudits.length === 0 ? (
            <Card className="border border-slate-200 rounded-xl">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No completed audits yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {completedAudits.map((audit) => {
                const typeInfo = getAuditTypeInfo(audit.audit_type);
                const Icon = typeInfo.icon;
                return (
                  <Card key={audit.id} className="border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 ${typeInfo.color} rounded-xl flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-slate-900">{typeInfo.name}</h3>
                              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                                Completed
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{getStoreName(audit.store_id)}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(audit.completed_at || audit.created_at), 'MMM d, yyyy')}
                              </span>
                              <span className="flex items-center gap-1.5" data-testid={`audit-creator-${audit.id}`}>
                                <UserAvatar name={audit.created_by} picture={audit.created_by_picture} size="xs" />
                                {audit.created_by}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          {audit.score !== undefined && (
                            <div className="text-2xl font-bold text-green-600">{audit.score}%</div>
                          )}
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleContinueAudit(audit)}
                            className="mt-2 rounded-lg"
                          >
                            View Report
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Start Audit Dialog */}
      <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Start New Audit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Select Store</label>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Choose a store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Audit Type</label>
              <Select value={selectedAuditType} onValueChange={setSelectedAuditType}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Choose audit type" />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleStartAudit}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-11"
              disabled={!selectedStore || !selectedAuditType}
            >
              Start Audit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Form Dialog - Simplified for now */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl">
          {currentAudit && (
            <AuditForm 
              audit={currentAudit}
              storeName={getStoreName(currentAudit.store_id)}
              typeInfo={getAuditTypeInfo(currentAudit.audit_type)}
              onSave={handleSaveAudit}
              onClose={() => setAuditDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Audit Form Component
function AuditForm({ audit, storeName, typeInfo, onSave, onClose }) {
  const [responses, setResponses] = useState(audit.responses || {});
  const [actionPlans, setActionPlans] = useState(audit.action_plans || {});
  const [photos, setPhotos] = useState(audit.photos || {});
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Corrective action dialog state
  const [actionDialog, setActionDialog] = useState({ open: false, itemId: null, question: '' });
  const [actionText, setActionText] = useState('');
  const [actionPhoto, setActionPhoto] = useState(null);

  // Photo dialog state  
  const [photoDialog, setPhotoDialog] = useState({ open: false, itemId: null, question: '' });

  const actionCameraRef = React.useRef(null);
  const actionGalleryRef = React.useRef(null);
  const photoCameraRef = React.useRef(null);
  const photoGalleryRef = React.useRef(null);
  const planCheckEnabled = actionDialog.open && actionText.trim().length >= 6;
  const { suggestion: planCheck, loading: planChecking } = useJevSuggest('action-plan', { question: actionDialog.question, plan: actionText }, planCheckEnabled);

  
  // Get questions for this audit type
  const questions = AUDIT_QUESTIONS[audit.audit_type];
  
  if (!questions) {
    return (
      <div className="text-center py-12 text-slate-500">
        <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-lg font-medium">Questions not configured</p>
        <p className="text-sm">Contact admin to set up this audit type</p>
      </div>
    );
  }

  const totalItems = questions.sections.reduce((sum, s) => sum + s.items.length, 0);
  const answeredItems = Object.keys(responses).length;
  const progress = totalItems > 0 ? Math.round((answeredItems / totalItems) * 100) : 0;

  const handleResponse = (itemId, value, question) => {
    if (value === 'no') {
      // Open corrective action dialog
      setActionText(actionPlans[itemId] || '');
      setActionPhoto(photos[`${itemId}_action`] || null);
      setActionDialog({ open: true, itemId, question });
    } else {
      setResponses(prev => ({ ...prev, [itemId]: value }));
    }
  };

  const handleSaveAction = () => {
    if (!actionText.trim()) {
      return; // Require action plan text
    }
    setResponses(prev => ({ ...prev, [actionDialog.itemId]: 'no' }));
    setActionPlans(prev => ({ ...prev, [actionDialog.itemId]: actionText }));
    if (actionPhoto) {
      setPhotos(prev => ({ ...prev, [`${actionDialog.itemId}_action`]: actionPhoto }));
    }
    setActionDialog({ open: false, itemId: null, question: '' });
    setActionText('');
    setActionPhoto(null);
  };

  const handlePhotoUpload = async (e, targetType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload`, formData, { timeout: 60000 });
      if (targetType === 'action') {
        setActionPhoto(res.data.url);
      } else if (targetType === 'task') {
        setPhotos(prev => ({ ...prev, [photoDialog.itemId]: res.data.url }));
        setPhotoDialog({ open: false, itemId: null, question: '' });
      }
    } catch {
      // Photo upload failed silently
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (complete = false) => {
    setSaving(true);
    await onSave(responses, complete, actionPlans, photos);
    setSaving(false);
  };

  const currentSection = questions.sections[activeSection];
  const isReadOnly = audit.status === 'completed';

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between">
          <div>
            <DialogTitle className="text-lg">{questions.name}</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">{storeName}</p>
          </div>
          {audit.status === 'completed' ? (
            <Badge className="bg-green-100 text-green-700">
              Score: {audit.score || 0}%
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-600">
              {progress}% Complete
            </Badge>
          )}
        </div>
      </DialogHeader>

      {/* Progress Bar */}
      <div className="mt-4">
        <Progress value={progress} className="h-2" />
        <p className="text-xs text-slate-500 mt-1">{answeredItems} of {totalItems} items</p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
        {questions.sections.map((section, idx) => {
          const sectionAnswered = section.items.filter(i => responses[i.id] !== undefined).length;
          const sectionComplete = sectionAnswered === section.items.length;
          return (
            <button
              key={idx}
              onClick={() => setActiveSection(idx)}
              className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                activeSection === idx
                  ? 'bg-orange-500 text-white'
                  : sectionComplete
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {section.name}
              {sectionComplete && <CheckCircle className="w-4 h-4" />}
            </button>
          );
        })}
      </div>

      {/* Questions */}
      <div className="mt-6 space-y-4">
        <h3 className="font-semibold text-slate-900">{currentSection.name}</h3>
        {currentSection.items.map((item, idx) => (
          <div 
            key={item.id} 
            className={`p-4 rounded-xl border ${
              item.critical 
                ? 'border-red-200 bg-red-50' 
                : responses[item.id] === 'no'
                ? 'border-red-300 bg-red-50'
                : 'border-slate-200 bg-white'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm text-slate-700">
                  {idx + 1}. {item.question}
                </p>
                {item.critical && (
                  <span className="text-xs text-red-600 font-medium mt-1 inline-block">
                    CRITICAL ITEM
                  </span>
                )}
                {/* Show action plan if "No" was selected */}
                {responses[item.id] === 'no' && actionPlans[item.id] && (
                  <div className="mt-2 p-2 bg-red-100 rounded-lg border border-red-200">
                    <p className="text-xs font-semibold text-red-700">Corrective Action:</p>
                    <p className="text-xs text-red-600">{actionPlans[item.id]}</p>
                    {photos[`${item.id}_action`] && (
                      <img src={photos[`${item.id}_action`]} alt="Action evidence" className="mt-1 w-16 h-16 object-cover rounded border" />
                    )}
                  </div>
                )}
                {/* Show task photo if attached */}
                {photos[item.id] && (
                  <div className="mt-2">
                    <img src={photos[item.id]} alt="Evidence" className="w-16 h-16 object-cover rounded border" />
                  </div>
                )}
              </div>
              {!isReadOnly ? (
                <div className="flex items-center gap-1">
                  {/* Photo button */}
                  <button
                    onClick={() => setPhotoDialog({ open: true, itemId: item.id, question: item.question })}
                    className={`p-2 rounded-lg text-sm transition-all ${
                      photos[item.id] ? 'bg-blue-100 text-blue-600' : 'bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-500'
                    }`}
                    title="Attach photo"
                    data-testid={`photo-btn-${item.id}`}
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleResponse(item.id, 'yes', item.question)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      responses[item.id] === 'yes'
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-green-100'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => handleResponse(item.id, 'no', item.question)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      responses[item.id] === 'no'
                        ? 'bg-red-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-red-100'
                    }`}
                  >
                    No
                  </button>
                  <button
                    onClick={() => handleResponse(item.id, 'na', item.question)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      responses[item.id] === 'na'
                        ? 'bg-slate-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    N/A
                  </button>
                </div>
              ) : (
                <Badge 
                  className={
                    responses[item.id] === 'yes' 
                      ? 'bg-green-100 text-green-700' 
                      : responses[item.id] === 'no'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-slate-100 text-slate-600'
                  }
                >
                  {responses[item.id]?.toUpperCase() || 'Not Answered'}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
            disabled={activeSection === 0}
            className="rounded-xl"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setActiveSection(Math.min(questions.sections.length - 1, activeSection + 1))}
            disabled={activeSection === questions.sections.length - 1}
            className="rounded-xl"
          >
            Next
          </Button>
        </div>

        {!isReadOnly && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="rounded-xl"
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Progress
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600 text-white rounded-xl"
              onClick={() => handleSave(true)}
              disabled={saving || progress < 100}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Complete Audit
            </Button>
          </div>
        )}
      </div>

      {/* Corrective Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => { if (!open) setActionDialog({ open: false, itemId: null, question: '' }); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Corrective Action Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">Issue Found:</p>
              <p className="text-sm text-red-700">{actionDialog.question}</p>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Corrective Action Plan <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                placeholder="Describe the corrective action taken or planned..."
                className="w-full mt-1 p-3 border border-red-200 rounded-lg text-sm min-h-[80px] focus:outline-none focus:border-red-400"
                data-testid="corrective-action-input"
              />
              {(planChecking || planCheck) && (
                <div className="mt-2 space-y-1.5" data-testid="action-plan-quality">
                  <div className="flex items-center gap-2 text-xs">
                    <Sparkles className={`w-3.5 h-3.5 text-orange-500 ${planChecking ? 'animate-pulse' : ''}`} />
                    {planChecking && !planCheck ? <span className="text-slate-400">Checking your plan…</span> : (
                      <>
                        <span className="text-slate-600">Plan quality:</span>
                        <div className="flex gap-0.5">
                          {['vague', 'partial', 'specific'].map((lvl, i) => {
                            const reached = ['vague', 'partial', 'specific'].indexOf(planCheck.quality.value) >= i;
                            const color = planCheck.quality.value === 'specific' ? 'bg-green-500' : planCheck.quality.value === 'partial' ? 'bg-amber-400' : 'bg-red-400';
                            return <span key={lvl} className={`h-1.5 w-6 rounded-full ${reached ? color : 'bg-slate-200'}`} />;
                          })}
                        </div>
                        <span className={`font-semibold capitalize ${planCheck.quality.value === 'specific' ? 'text-green-700' : planCheck.quality.value === 'partial' ? 'text-amber-700' : 'text-red-600'}`} data-testid="action-plan-quality-label">{planCheck.quality.value}</span>
                        {planCheck.food_safety_risk >= 0.7 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" data-testid="action-plan-food-safety">
                            <AlertTriangle className="w-3 h-3" /> Food safety
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {planCheck?.tips?.length > 0 && (
                    <p className="text-xs text-slate-500">Tip: {planCheck.tips.join(' · ')}</p>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Photo Evidence (optional)</label>
              {actionPhoto ? (
                <div className="relative mt-1">
                  <img src={actionPhoto} alt="Evidence" className="w-full h-28 object-cover rounded-lg border" />
                  <button onClick={() => setActionPhoto(null)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button onClick={() => actionCameraRef.current?.click()} disabled={uploading}
                    className="flex items-center justify-center gap-2 p-3 border border-dashed border-orange-300 rounded-lg hover:bg-orange-50">
                    <input ref={actionCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoUpload(e, 'action')} />
                    <Camera className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-orange-700">Camera</span>
                  </button>
                  <button onClick={() => actionGalleryRef.current?.click()} disabled={uploading}
                    className="flex items-center justify-center gap-2 p-3 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50">
                    <input ref={actionGalleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'action')} />
                    <Upload className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-blue-700">Gallery</span>
                  </button>
                </div>
              )}
              {uploading && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActionDialog({ open: false, itemId: null, question: '' })} className="rounded-lg">Cancel</Button>
              <Button onClick={handleSaveAction} disabled={!actionText.trim()} className="bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="save-corrective-action-btn">
                Save Corrective Action
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialog.open} onOpenChange={(open) => { if (!open) setPhotoDialog({ open: false, itemId: null, question: '' }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Attach Photo Evidence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{photoDialog.question}</p>
            {photos[photoDialog.itemId] && (
              <div className="relative">
                <img src={photos[photoDialog.itemId]} alt="Current" className="w-full h-32 object-cover rounded-lg border" />
                <p className="text-xs text-green-600 mt-1 text-center">Photo attached</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => photoCameraRef.current?.click()} disabled={uploading}
                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-orange-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 cursor-pointer">
                <input ref={photoCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoUpload(e, 'task')} />
                <Camera className="w-10 h-10 mb-2 text-orange-500" />
                <p className="text-sm font-semibold text-orange-700">Take Photo</p>
                <p className="text-xs text-orange-400">Use camera</p>
              </button>
              <button onClick={() => photoGalleryRef.current?.click()} disabled={uploading}
                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 cursor-pointer">
                <input ref={photoGalleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'task')} />
                <Upload className="w-10 h-10 mb-2 text-blue-500" />
                <p className="text-sm font-semibold text-blue-700">Upload Photo</p>
                <p className="text-xs text-blue-400">From gallery</p>
              </button>
            </div>
            {uploading && <p className="text-sm text-slate-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
